import { inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';
import { ClientLogEvent, LogSessionContext, QueuedLogEvent } from '../../shared/models/be/system-log';
import { createUuid } from '../../shared/utils/uuid.util';

interface Transition {
  key: string;
  online: boolean;
  interruptionId?: string;
  sequence: number;
}

export const LOG_DATABASE_NAME = new InjectionToken<string>('LOG_DATABASE_NAME', { providedIn: 'root', factory: () => 'wita-system-logs' });

/** IndexedDB transactions coordinate event production across browser tabs. */
@Injectable({ providedIn: 'root' })
export class LogEventStore implements OnDestroy {
  private database?: Promise<IDBDatabase>;
  private readonly databaseName = inject(LOG_DATABASE_NAME);

  private open(): Promise<IDBDatabase> {
    return this.database ??= new Promise((resolve, reject) => {
      const request = indexedDB.open(this.databaseName, 1);
      request.onupgradeneeded = () => {
        const events = request.result.createObjectStore('events', { keyPath: 'id' });
        events.createIndex('userId', 'userId');
        request.result.createObjectStore('transitions', { keyPath: 'key' });
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => { this.database = undefined; reject(request.error); };
    });
  }

  private async write(stores: string[], action: (transaction: IDBTransaction) => void): Promise<void> {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(stores, 'readwrite');
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
      action(transaction);
    });
  }

  async add(userId: number, event: ClientLogEvent): Promise<void> {
    await this.write(['events'], tx => tx.objectStore('events').add({ id: event.id, userId, event, state: 'pending' } satisfies QueuedLogEvent));
  }

  async pending(userId: number, limit: number): Promise<QueuedLogEvent[]> {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const rows: QueuedLogEvent[] = [];
      const request = db.transaction('events').objectStore('events').index('userId').openCursor(IDBKeyRange.only(userId));
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor || rows.length >= limit) { resolve(rows); return; }
        const row = cursor.value as QueuedLogEvent;
        if (row.state === 'pending') rows.push(row);
        cursor.continue();
      };
    });
  }

  async settle(rows: QueuedLogEvent[], remove: Set<string>, rejected: Map<string, string>): Promise<void> {
    await this.write(['events'], tx => {
      const store = tx.objectStore('events');
      for (const row of rows) {
        if (remove.has(row.id)) store.delete(row.id);
        else if (rejected.has(row.id)) store.put({ ...row, state: 'quarantined', rejectionCode: rejected.get(row.id) });
      }
    });
  }

  async transition(context: LogSessionContext, kind: 'network' | 'backend', online: boolean, metadata: Record<string, string | number> = {}): Promise<void> {
    // Capture the observation time before awaiting database access.
    const occurredAt = new Date().toISOString();
    const key = `${context.userId}:${context.sessionId}:${context.installationId}:${kind}`;
    await this.write(['events', 'transitions'], tx => {
      const transitions = tx.objectStore('transitions');
      const request = transitions.get(key);
      request.onsuccess = () => {
        const previous = request.result as Transition | undefined;
        if (previous?.online === online) return;
        if (!previous && online) {
          transitions.put({ key, online, sequence: 0 } satisfies Transition);
          return;
        }
        const interruptionId = online ? previous?.interruptionId : createUuid();
        const sequence = online ? (previous?.sequence ?? 0) + 1 : 1;
        transitions.put({ key, online, interruptionId, sequence } satisfies Transition);
        const { userId, ...clientContext } = context;
        const event: ClientLogEvent = {
          ...clientContext, id: createUuid(), occurredAt,
          eventType: kind === 'network' ? (online ? 'NETWORK_RESTORED' : 'NETWORK_LOST') : (online ? 'BACKEND_REACHABLE_AGAIN' : 'BACKEND_UNREACHABLE'),
          reasonCode: 'DEVICE_OBSERVATION',
          metadata: { ...metadata, ...(interruptionId ? { interruptionId } : {}), sequence }
        };
        tx.objectStore('events').add({ id: event.id, userId, event, state: 'pending' } satisfies QueuedLogEvent);
      };
    });
  }

  ngOnDestroy(): void { void this.database?.then(db => db.close()); }
}
